// Inner content of the account popup. Rendered inside a Google Maps InfoWindow,
// which provides its own anchor/close UI — this component only renders the data.

import { useQuery } from '@tanstack/react-query';
import { api, type PopupPreset, type PopupSize } from '../lib/api';

interface AccountPopupProps {
  accountId: string;
  preset: PopupPreset;
  size?: PopupSize;
  pinned: boolean;
  onTogglePin: () => void;
  onClose: () => void;
}

// Static class strings per size so Tailwind's JIT keeps them (dynamic
// `min-w-[${n}]` would be purged). Rows/gaps also shrink with the container.
const SIZE_STYLES: Record<PopupSize, {
  container: string;
  title: string;
  label: string;
  value: string;
  rows: string;
}> = {
  small: {
    container: 'min-w-[150px] max-w-[220px]',
    title: 'text-xs',
    label: 'text-[9px]',
    value: 'text-xs',
    rows: 'space-y-1',
  },
  medium: {
    container: 'min-w-[240px] max-w-[320px]',
    title: 'text-sm',
    label: 'text-[10px]',
    value: 'text-sm',
    rows: 'space-y-1.5',
  },
  large: {
    container: 'min-w-[320px] max-w-[440px]',
    title: 'text-base',
    label: 'text-[11px]',
    value: 'text-base',
    rows: 'space-y-2',
  },
};

export default function AccountPopup({
  accountId,
  preset,
  size = 'medium',
  pinned,
  onTogglePin,
  onClose,
}: AccountPopupProps) {
  const styles = SIZE_STYLES[size] ?? SIZE_STYLES.medium;
  const { data, isLoading, error } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => api.getAccount(accountId),
    staleTime: 5 * 60_000,
  });

  const account = data?.account;

  function getFieldValue(fieldName: string): string {
    if (!account) return '';
    const builtIn: Record<string, unknown> = {
      name: account.name,
      address: account.address,
      city: account.city,
      state: account.state,
      zipCode: account.zipCode,
      lat: account.lat,
      lng: account.lng,
    };
    if (fieldName in builtIn) {
      return String(builtIn[fieldName] ?? '');
    }
    const val = account.fields[fieldName];
    if (val == null) return '';
    return String(val);
  }

  function getTitle(): string {
    return getFieldValue(preset.popupTitle) || 'Account';
  }

  return (
    <div className={styles.container}>
      <div className="flex items-start justify-between mb-2 gap-2">
        <h3 className={`font-display ${styles.title} font-semibold text-brand-700 flex-1 min-w-0`}>
          {isLoading ? 'Loading...' : getTitle()}
        </h3>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onTogglePin}
            aria-label={pinned ? 'Unpin popup' : 'Pin popup (keep open)'}
            title={pinned ? 'Unpin (close when clicking another pin)' : 'Pin (stay open)'}
            className={`rounded p-0.5 transition-colors ${
              pinned ? 'text-brand-600' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {pinned ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 3v7l3 3v2h-6v7l-1 1-1-1v-7H5v-2l3-3V3h8Z" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 3v7l3 3v2h-6v7l-1 1-1-1v-7H5v-2l3-3V3h8Z" />
              </svg>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close popup"
            title="Close"
            className="rounded p-0.5 text-gray-400 hover:text-gray-700"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading account details...</p>}

      {error && (
        <p className="text-sm text-red-600">
          Failed to load: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      )}

      {account?.crmUrl && (
        <a href={account.crmUrl} target="_blank" rel="noopener" className={`${styles.label} mb-1.5 inline-block text-brand-600 hover:text-brand-700 hover:underline`}>
          Open in CRM ↗
        </a>
      )}

      {account && (
        <dl className={styles.rows}>
          {preset.popupFields.map((field) => {
            const value = getFieldValue(field);
            if (!value) return null;
            return (
              <div key={field}>
                <dt className={`${styles.label} font-medium text-gray-500 uppercase tracking-wide`}>
                  {field.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()}
                </dt>
                <dd className={`${styles.value} text-gray-900`}>{value}</dd>
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
}
